<script setup lang="ts">
import { useSnackbarStore } from '@/store/snackbar'
import VuePdfEmbed from 'vue-pdf-embed'
import { $signAnnotationCreate, $signAnnotationDelete, $signAnnotationUpdate } from '@/api/signs'
import ability from '@/plugins/casl/ability'

interface IProps {
  sign: any,
  annotations: any[],
  artworkFileCurrentPage: number,
  artworkFileLoading: boolean,
  pdfScale: number,
  headerHeight: number,
}

const props = withDefaults(defineProps<IProps>(), {
  sign: null,
  annotations: () => ([]),
  artworkFileCurrentPage: 1,
  artworkFileLoading: true,
  pdfScale: 100,
  headerHeight: 48.5,
})
interface Emit {
  (e: 'update:annotations', value: any): void
  (e: 'update:artworkFileCurrentPage', value: number): void
  (e: 'update:artworkFileLoading', value: boolean): void
  (e: 'getAnnotations'): void
  (e: 'onSetHover', value: any): void
}

const emit = defineEmits<Emit>()

const snackbarStore = useSnackbarStore()

interface MapPosition { left: number; top: number; x: number; y: number; }

const artworkFilePdf = ref<InstanceType<typeof VuePdfEmbed> | null>(null)
const artworkFilePageCount = ref<number>(1)
const isMoving = ref<boolean>(false)
const artworkFileMapPos = ref<MapPosition>({ left: 0, top: 0, x: 0, y: 0 })

const isCanAddAnnotation = computed<boolean>(() => {
  return ability.can('add', 'signartworkannot')
})

const isCanChangeAnnotation = computed<boolean>(() => {
  return ability.can('change', 'signartworkannot')
})

const isCanDeleteAnnotation = computed<boolean>(() => {
  return ability.can('delete', 'signartworkannot')
})

const isCanResolveAnnotation = computed<boolean>(() => {
  return ability.can('resolve', 'signartworkannot')
})

const artworkFileUrl = computed(() => {
  if (props.sign?.artwork_link) {
    return props.sign.artwork_link
  } else {
    return null
  }
})

const isShownAnyAnnotationMenu = computed(() => {
  return !!props.annotations
    .filter(annot => annot.page === props.artworkFileCurrentPage && !!annot.isShownAnnotationMenu).length
})

const addAnnotation = (newAnnotation: any) => {
  const updatedAnnotations = [...props.annotations, newAnnotation]
  emit('update:annotations', updatedAnnotations)
}

const hideAnnotationMenus = () => {
  emit('update:annotations', props.annotations.map(i => ({ ...i, isShownAnnotationMenu: false }) ))
}

const createAnnotation = async (event: MouseEvent) => {
  if (!isCanAddAnnotation.value) return

  if (!props?.sign?.id) return

  const textLayer = document.getElementsByClassName('textLayer')[0] as HTMLElement

  if (
    event.target === textLayer
    && !(event.target as HTMLElement).hasAttribute('annot')
    && !isShownAnyAnnotationMenu.value
  ) {
    // Calculate percentages
    const topPerc = ((event.offsetY - 12) / (textLayer?.offsetHeight || 100)) * 100
    const leftPerc = ((event.offsetX - 12) / (textLayer?.offsetWidth || 100)) * 100

    const payload = {
      sign: props.sign.id,
      top: topPerc,
      left: leftPerc,
      page: props.artworkFileCurrentPage
    }
    try {
      const res = await $signAnnotationCreate(payload)
      addAnnotation(res.data)
    } catch (e) {
      console.log(e)
    }
  } else if (isShownAnyAnnotationMenu.value) {
    hideAnnotationMenus()
  }
}

const updateAnnotation = async (annotation: any) => {
  try {
    await $signAnnotationUpdate(annotation.id, annotation)
    hideAnnotationMenus()
    snackbarStore.showMessage({
      color: 'success',
      icon: 'mdi-checkbox-marked-circle',
      title: 'Success',
      text: 'Annotation saved successfully!'
    })
  } catch (e) {
    console.log(e)
  }
}

const deleteAnnotation = async (id: number) => {
  try {
    await $signAnnotationDelete(id)
    emit('getAnnotations')
    hideAnnotationMenus()
    snackbarStore.showMessage({
      color: 'success',
      icon: 'mdi-checkbox-marked-circle',
      title: 'Success',
      text: 'Annotation deleted successfully!'
    })
  } catch (e) {
    console.log(e)
  }
}

const onMoveMapStart = (e: MouseEvent, mapPos: MapPosition, elementId: string) => {
  // On mouse left button (primary) - move PDF
  if (e.button === 0) {
    const el = document.getElementById(elementId) as HTMLElement
    isMoving.value = true
    mapPos.left = el.scrollLeft
    mapPos.top = el.scrollTop
    mapPos.x = e.clientX
    mapPos.y = e.clientY
  }
}

const onMoveMap = (e: MouseEvent, isCenter: boolean, mapPos: MapPosition, elementId: string) => {
  if (isCenter) {
    setTimeout(() => {
      const containerIds = ['artwork-file-pdf-container-id']
      containerIds.forEach((containerId: string) => {
        const container = document.getElementById(containerId) as HTMLElement
        container.scrollTop = container.scrollHeight / 2 - container.clientHeight / 2
        container.scrollLeft = container.scrollWidth / 2 - container.clientWidth / 2
      })
    }, 0)
  } else {
    const el = document.getElementById(elementId) as HTMLElement
    const dx = e.clientX - mapPos.x
    const dy = e.clientY - mapPos.y

    el.scrollTop = mapPos.top - dy
    el.scrollLeft = mapPos.left - dx
  }
}

</script>

<template>
  <VRow
    ref="artworksBlock"
    class="artwork-row mx-0"
    :style="{ '--header-height': `${headerHeight}px` }"
  >
    <VCol
      class="position-relative py-0 px-1"
      :class="{ 'min-height-200': artworkFileLoading }"
    >
      <div
        v-if="artworkFileUrl"
        style="margin: auto; max-height: inherit"
      >
        <VOverlay
          :model-value="artworkFileLoading"
          persistent
          absolute
          contained
          class="align-center justify-center"
        >
          <VProgressCircular
            indeterminate
            color="primary"
          />
        </VOverlay>
        <div class="text-center">
          <VBtn
            size="sm"
            class="p-0 rounded-circle"
            pill
            color="light"
            :disabled="artworkFileCurrentPage === 1"
            @click="emit('update:artworkFileCurrentPage', artworkFileCurrentPage - 1)"
          >
            <VIcon icon="mdi-arrow-left-bold-circle-outline" />
          </VBtn>
          <span>
            {{ artworkFileCurrentPage }}
          </span>
          <VBtn
            size="sm"
            class="p-0 rounded-circle"
            pill
            color="light"
            :disabled="artworkFileCurrentPage === artworkFilePageCount"
            @click="emit('update:artworkFileCurrentPage', artworkFileCurrentPage + 1)"
          >
            <VIcon icon="mdi-arrow-right-bold-circle-outline" />
          </VBtn>
        </div>
        <div
          id="artwork-file-pdf-container-id"
          class="artwork-file-pdf-container"
          @mousedown="onMoveMapStart($event, artworkFileMapPos, 'artwork-file-pdf-container-id')"
          v-on="{ mousemove: (event) => { if (isMoving) onMoveMap(event, false, artworkFileMapPos, 'artwork-file-pdf-container-id') } }"
          @mouseup="isMoving = false"
          @mouseout="isMoving = false"
        >
          <div
            class="artwork-file-pdf-wrapper overflow-hidden"
            :style="{ width: `${pdfScale}%` }"
            @click.right.prevent="(event) => { createAnnotation(event) }"
          >
            <VuePdfEmbed
              ref="artworkFilePdf"
              id="artwork-file-pdf"
              textLayer
              :source="artworkFileUrl"
              :page="artworkFileCurrentPage"
              @rendered="emit('update:artworkFileLoading', false); artworkFilePageCount = artworkFilePdf.pageCount"
            />
            <div
              v-for="(annotation, key) in annotations.filter(annot => annot.page === artworkFileCurrentPage)"
              :id="`annotation${key}`"
              :key="key"
              class="pdfAnnotation"
              :class="{ 'hovered': annotation.hover, 'd-none': artworkFileLoading }"
              :style="{ left: annotation.left + '%', top: annotation.top + '%' }"
              annot="true"
              @mouseover="emit('onSetHover', { annotation: annotation, status: true })"
              @mouseout="emit('onSetHover', { annotation: annotation, status: false })"
            >
              <span annot="true">
                {{ annotation.index }}
              </span>
              <VMenu
                v-model="annotation.isShownAnnotationMenu"
                :id="'pop' + annotation.id"
                :activator="`#annotation${key}`"
                :open-on-click="true"
                :open-on-hover="false"
                :close-on-content-click="false"
                location="top center"
                offset="5"
              >
                <VCard>
                  <VCardText
                    class="border rounded-lg pa-3"
                  >
                    <VTextarea
                      v-model="annotation.comment"
                      placeholder="Enter comments..."
                      class="mb-1"
                      rows="3"
                      :disabled="annotation.is_resolved || !isCanChangeAnnotation"
                    />
                    <div
                      v-if="!annotation.is_resolved"
                      class="mt-1 d-flex"
                    >
                      <div
                        v-if="isCanChangeAnnotation"
                        class="save-btn"
                      >
                        <VBtn
                          color="success"
                          :disabled="!annotation.comment"
                          @click="updateAnnotation(annotation)"
                        >
                          Save
                        </VBtn>
                      </div>
                      <VTooltip
                        v-if="!annotation.comment"
                        activator=".save-btn"
                        location="top center"
                        offset="5"
                        class="default-tooltip"
                      >
                        <span>
                          Please, add comment
                        </span>
                      </VTooltip>
                      <div
                        v-if="isCanResolveAnnotation"
                        class="set-resolved-btn">
                        <VBtn
                          class="ml-1"
                          color="info"
                          :disabled="!annotation.comment"
                          @click="annotation.is_resolved=true; updateAnnotation(annotation)"
                        >
                          Set Resolved
                        </VBtn>
                      </div>
                      <VTooltip
                        v-if="!annotation.comment"
                        activator=".set-resolved-btn"
                        location="top center"
                        offset="5"
                        class="default-tooltip"
                      >
                        <span>
                          Please, add comment
                        </span>
                      </VTooltip>
                      <VBtn
                        v-if="isCanDeleteAnnotation"
                        class="ml-1"
                        @click="deleteAnnotation(annotation.id)"
                      >
                        Delete
                      </VBtn>
                    </div>
                  </VCardText>
                </VCard>
              </VMenu>
            </div>
          </div>
        </div>
      </div>
      <div
        v-else-if="!artworkFileUrl"
        style="margin: auto;"
      >
        <h4>File not provided</h4>
      </div>
    </VCol>
  </VRow>
</template>

<style lang="scss">
.artwork-file-pdf-container {
  overflow: scroll;
  max-width: 100%;
  // max-height: display size - header height - top and bottom dialog padding - artwork file pagination height
  max-height: calc(100vh - var(--header-height) - 40px - 22.5px);
  .artwork-file-pdf-wrapper {
    position: relative;
    margin: 0 auto;
    &:active {
      cursor: all-scroll;
    }
    .pdfAnnotation {
      position: absolute;
      display: block;
      width: 24px;
      height: 24px;
      background: #dd082b;
      border-radius: 50%;
      text-align: center;
      color: #fff;
      cursor: pointer;
      &.hovered {
        background: #fff;
        color: #dd082b;
        border: 1px solid #dd082b;
      }
    }
  }
}

canvas {
  width: 100% !important;
  height: unset !important;
}

.artwork-row {
  max-height: inherit;
  position: relative;
  > .v-col {
    max-height: inherit;
  }
}

.sign-artwork-review-pdf-pagination-block {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translate(0, -50%);
}
</style>
